import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface UsePwaInstallResult {
  canInstall: boolean;
  isInstalled: boolean;
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

/**
 * Web-Install-Prompt-Hook (Chromium-based browsers). Listens to
 * `beforeinstallprompt`, speichert das Event und exposed eine `install()`-
 * Funktion, die den nativen Prompt triggert.
 *
 * iOS-Safari unterstützt das Event nicht — dort muss der User manuell über
 * "Teilen → Zum Home-Bildschirm" gehen. Wir blenden dann den Button aus.
 */
export function usePwaInstall(): UsePwaInstallResult {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => isStandalone());

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    }
    function onAppInstalled() {
      setIsInstalled(true);
      setPromptEvent(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return 'unavailable' as const;
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      setPromptEvent(null);
      return choice.outcome;
    } catch {
      return 'unavailable' as const;
    }
  }, [promptEvent]);

  return {
    canInstall: !!promptEvent && !isInstalled,
    isInstalled,
    install,
  };
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari Legacy.
  return !!(window.navigator as Navigator & { standalone?: boolean }).standalone;
}
